import React, { useEffect, useState } from 'react';
import firebase from "../../firebaseConfig";
import "../../styles/generalTabs.css";
import { FaTrash, FaEdit, FaCheckSquare, FaRegSquare, FaPlusCircle } from 'react-icons/fa';
import { TiArrowSortedDown, TiArrowSortedUp } from 'react-icons/ti';

const priorityColors = {
    "High": "#ff7675",
    "Medium": "#fdcb6e",
    "Low": "#74b9ff"
};

const categories = ["Professional", "Personal", "Health", "Leisure", "Learning", "Other"];
const priorities = ["High", "Medium", "Low"];

const DailyTodoList = ({ user }) => {
    const [todoItems, setTodoItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTask, setNewTask] = useState({
        task: "",
        description: "",
        priority: "Medium",
        category: "Professional",
        completed: false
    });
    const [editMode, setEditMode] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [filterCategory, setFilterCategory] = useState("All");
    const [sortBy, setSortBy] = useState("priority");
    const [sortDirection, setSortDirection] = useState("desc");
    const [error, setError] = useState("");

    useEffect(() => {
        fetchTodoData();
    }, [user]);

    const fetchTodoData = async () => {
        try {
            if (user) {
                const userId = user.uid;
                const docRef = firebase.firestore().collection('users').doc(userId);
                const doc = await docRef.get();
                
                if (doc.exists) {
                    const userData = doc.data();
                    if (userData.DailyTodoListData && userData.DailyTodoListData.data) {
                        // Convertir les données en tableau si besoin
                        const items = Array.isArray(userData.DailyTodoListData.data) 
                            ? userData.DailyTodoListData.data 
                            : Object.values(userData.DailyTodoListData.data);
                        
                        setTodoItems(items);
                    } else {
                        // Si pas de données, initialiser avec un tableau vide
                        setTodoItems([]);
                    }
                }
                setLoading(false);
            }
        } catch (error) {
            console.error("Error while retrieving tasks:", error);
            setLoading(false);
            setError("Error loading tasks");
        }
    };

    const saveTodoData = async (newItems) => {
        try {
            if (user) {
                const userId = user.uid;
                const docRef = firebase.firestore().collection('users').doc(userId);
                
                await docRef.update({
                    "DailyTodoListData.data": newItems
                });
                
                setTodoItems(newItems);
            }
        } catch (error) {
            console.error("Error while saving tasks:", error);
            setError("Error saving tasks");
        }
    };

    const handleAddTask = () => {
        if (!newTask.task.trim()) {
            setError("The task cannot be empty");
            return;
        }
        
        setError("");
        const updatedItems = [...todoItems, { ...newTask }];
        saveTodoData(updatedItems);
        setNewTask({
            task: "",
            description: "",
            priority: "Medium",
            category: "Professional",
            completed: false
        });
    };

    const handleEditTask = (index) => {
        setEditMode(true);
        setEditingIndex(index);
        setNewTask({ ...todoItems[index] });
    };

    const handleUpdateTask = () => {
        if (!newTask.task.trim()) {
            setError("The task cannot be empty");
            return;
        }
        
        setError("");
        const updatedItems = [...todoItems];
        updatedItems[editingIndex] = { ...newTask };
        saveTodoData(updatedItems);
        
        setEditMode(false);
        setEditingIndex(null);
        setNewTask({
            task: "",
            description: "",
            priority: "Medium",
            category: "Professional",
            completed: false
        });
    };

    const handleDeleteTask = (index) => {
        const updatedItems = todoItems.filter((_, i) => i !== index);
        saveTodoData(updatedItems);
    };

    const handleToggleComplete = (index) => {
        const updatedItems = [...todoItems];
        updatedItems[index].completed = !updatedItems[index].completed;
        saveTodoData(updatedItems);
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditingIndex(null);
        setNewTask({
            task: "",
            description: "",
            priority: "Medium",
            category: "Professional",
            completed: false
        });
    };

    const handleSortChange = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDirection("asc");
        }
    };

    const getSortedItems = () => {
        let filteredItems = todoItems;
        
        if (filterCategory !== "All") {
            filteredItems = filteredItems.filter(item => item.category === filterCategory);
        }
        
        return [...filteredItems].sort((a, b) => {
            if (sortBy === "priority") {
                const priorityValues = { "High": 3, "Medium": 2, "Low": 1 };
                const valueA = priorityValues[a.priority];
                const valueB = priorityValues[b.priority];
                
                return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
            } else if (sortBy === "category") {
                return sortDirection === "asc" 
                    ? a.category.localeCompare(b.category)
                    : b.category.localeCompare(a.category);
            } else if (sortBy === "completed") {
                return sortDirection === "asc" 
                    ? (a.completed ? 1 : 0) - (b.completed ? 1 : 0)
                    : (b.completed ? 1 : 0) - (a.completed ? 1 : 0);
            }
            return 0;
        });
    };

    const getSortIcon = (field) => {
        if (sortBy === field) {
            return sortDirection === "asc" ? <TiArrowSortedUp /> : <TiArrowSortedDown />;
        }
        return null;
    };

    if (loading) {
        return <div className="d-flex justify-content-center my-5"><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <div className="container my-4">
            <h2 className="text-center mb-4">Daily Todo List</h2>
            
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">{editMode ? "Edit Task" : "Add New Task"}</h5>
                    
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Task</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                value={newTask.task} 
                                onChange={(e) => setNewTask({...newTask, task: e.target.value})}
                                placeholder="Enter your task"
                            />
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">Category</label>
                            <select 
                                className="form-select"
                                value={newTask.category}
                                onChange={(e) => setNewTask({...newTask, category: e.target.value})}
                            >
                                {categories.map((category, index) => (
                                    <option key={index} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">Priority</label>
                            <select 
                                className="form-select"
                                value={newTask.priority}
                                onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                            >
                                {priorities.map((priority, index) => (
                                    <option key={index} value={priority}>{priority}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="col-12">
                            <label className="form-label">Description (optional)</label>
                            <textarea 
                                className="form-control" 
                                rows="2"
                                value={newTask.description} 
                                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                                placeholder="Add details about your task"
                            />
                        </div>
                        
                        <div className="col-12 d-flex justify-content-end">
                            {editMode ? (
                                <>
                                    <button className="btn btn-secondary me-2" onClick={handleCancelEdit}>Cancel</button>
                                    <button className="btn btn-primary" onClick={handleUpdateTask}>Update</button>
                                </>
                            ) : (
                                <button className="btn btn-primary" onClick={handleAddTask}>
                                    <FaPlusCircle className="me-2" />Add
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="card">
                <div className="card-header bg-light">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h5 className="mb-0">My Tasks</h5>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <div className="d-inline-block me-3">
                                <label className="me-2">Filter:</label>
                                <select 
                                    className="form-select form-select-sm d-inline-block w-auto"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    <option value="All">All categories</option>
                                    {categories.map((category, index) => (
                                        <option key={index} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="card-body">
                    {todoItems.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="text-muted">No tasks to display</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{width: "40px"}}></th>
                                        <th className="pointer" onClick={() => handleSortChange("task")}>
                                            Task {getSortIcon("task")}
                                        </th>
                                        <th className="pointer" onClick={() => handleSortChange("category")}>
                                            Category {getSortIcon("category")}
                                        </th>
                                        <th className="pointer" onClick={() => handleSortChange("priority")}>
                                            Priority <TiArrowSortedDown />
                                        </th>
                                        <th style={{width: "120px"}} className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedItems().map((item, index) => (
                                        <tr key={index} className={item.completed ? "table-light text-muted" : ""}>
                                            <td>
                                                <span 
                                                    className="pointer" 
                                                    onClick={() => handleToggleComplete(todoItems.indexOf(item))}
                                                >
                                                    {item.completed ? 
                                                        <FaCheckSquare className="text-success" /> : 
                                                        <FaRegSquare />}
                                                </span>
                                            </td>
                                            <td className={item.completed ? "text-decoration-line-through" : ""}>
                                                <div>{item.task}</div>
                                                {item.description && (
                                                    <small className="text-muted">{item.description}</small>
                                                )}
                                            </td>
                                            <td>{item.category}</td>
                                            <td>
                                                <span 
                                                    className="badge" 
                                                    style={{
                                                        backgroundColor: priorityColors[item.priority],
                                                        color: item.priority === "Medium" ? "#000" : "#fff"
                                                    }}
                                                >
                                                    {item.priority}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <button 
                                                    className="btn btn-sm btn-outline-primary me-1" 
                                                    onClick={() => handleEditTask(todoItems.indexOf(item))}
                                                    disabled={item.completed}
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger" 
                                                    onClick={() => handleDeleteTask(todoItems.indexOf(item))}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                
                <div className="card-footer text-muted">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>Total: {getSortedItems().length} task(s)</div>
                        <div>
                            Completed: {getSortedItems().filter(item => item.completed).length} / 
                            Ongoing: {getSortedItems().filter(item => !item.completed).length}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyTodoList;
